/**
 * Ownership context for fiat settlement detail sets.
 *
 * Labels ("Your account" / "Pooled account") should appear exactly when they
 * differentiate — i.e. when a pooled sibling exists in the same list. When
 * every set is in the customer's name, repeating "Your account" above each
 * card is what makes one account read as several.
 *
 * Mirrors the statement PDF's tested labelling rule exactly
 * (utils/fiatStatementPdfTemplate.tsx — "account headings only appear when
 * they disambiguate"): pooled sets are always labelled (a lone pooled set
 * unlabelled reads as the customer's own account), personal sets only when
 * there is exactly one of them beside a pooled sibling — repeating
 * "Your account" above several personal sets is the misleading repetition
 * this rule exists to remove.
 */

type MethodLike = { reference?: string | null };

export function isPooledMethod(method: MethodLike): boolean {
  return !!method.reference;
}

export type OwnershipContext = "personal" | "pooled" | null;

export function ownershipContext(
  method: MethodLike,
  methods: readonly MethodLike[]
): OwnershipContext {
  if (!methods.some(isPooledMethod)) {
    return null;
  }
  if (isPooledMethod(method)) {
    return "pooled";
  }
  const personalCount = methods.filter((m) => !isPooledMethod(m)).length;
  return personalCount === 1 ? "personal" : null;
}
